import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { QuarterSelectorStateService } from './quarter-selector-overlay/quarter-selector.state';
import { QuarterRangeSelectorState, QuarterSelectorState, QuarterSelectorValue } from './quarter-selector.model';

@Injectable()
export class RangeQuarterSelectorService implements OnDestroy {
  private currentState: QuarterRangeSelectorState;
  private onFinishSelectionSubject: Subject<void>;
  private onCloseSubject: Subject<void>;
  private minService: QuarterSelectorStateService;
  private maxService: QuarterSelectorStateService;
  public isAValidState = false;
  public isMaxStateValid = false;

  /**
   * This variable stores whether the state is single selection or not.
   * If it's not, we need to display both selectors.
   */
  public isSingleSelection = false;

  constructor() {
    this.onFinishSelectionSubject = new Subject();
    this.onCloseSubject = new Subject();
  }

  ngOnDestroy() {
    if (this.onFinishSelectionSubject) {
      this.onFinishSelectionSubject.complete();
      this.onFinishSelectionSubject = undefined;
    }

    if (this.onCloseSubject) {
      this.onCloseSubject.complete();
      this.onCloseSubject = undefined;
    }
  }

  public getMinService(): QuarterSelectorStateService {
    this.minService = new QuarterSelectorStateService();
    const value = this.isSingleSelection ? this.currentState.eq : this.currentState.gte;
    this.minService.setStateAndNotify(this.getSingleState(value));
    this.minService.onFinishSelection().subscribe(this.minValueChanged.bind(this));
    return this.minService;
  }

  private getSingleState(value: QuarterSelectorValue): QuarterSelectorState {
    return {
      periodValue: value ? value.periodValue : 0,
      periodYear: value ? value.periodYear : 0,
      selectorType: this.currentState.selectorType,
      requiredYear: this.currentState.requiredYear,
      maxValue: this.currentState.maxValue,
      minValue: this.currentState.minValue,
      showAsQtd: this.currentState.showAsQtd,
      allowedPeriods: this.currentState.allowedPeriods,
      restrictedPeriods: this.currentState.restrictedPeriods,
      marketQtdValue: this.currentState.marketQtdValue,
    };
  }

  public getMaxService(): QuarterSelectorStateService {
    this.maxService = new QuarterSelectorStateService();
    const currentMaxState = this.getSingleState(this.currentState.lte);
    if (!this.isSingleSelection && this.minService.isAValidState) {
      currentMaxState.minValue = {
        periodValue: this.minService.getState().periodValue,
        periodYear: this.minService.getState().periodYear,
      };
    }
    this.maxService.setStateAndNotify(currentMaxState);
    this.maxService.onFinishSelection().subscribe(this.maxValueChanged.bind(this));
    return this.maxService;
  }

  public onFinishSelection(): Observable<void> {
    return this.onFinishSelectionSubject.asObservable();
  }

  public onClose(): Observable<void> {
    return this.onCloseSubject.asObservable();
  }

  public setInitialState(state: QuarterRangeSelectorState) {
    this.currentState = state;
    this.isSingleSelection = this.analyzeSingleSelection();
    this.isAValidState = state.minValue != null;
    this.isMaxStateValid = state.maxValue != null;
  }

  public finishSelection() {
    this.onFinishSelectionSubject.next();
  }

  public close() {
    this.onCloseSubject.next();
  }

  public getState(): QuarterRangeSelectorState {
    return this.currentState;
  }

  /**
   * This method looks for the input type and return true if eq is undefined, which means that the
   * single selection mode is turned on.
   * else it returns false and the single selection mode is off.
   */
  private analyzeSingleSelection() {
    return this.currentState.singleSelection != null
      ? // Take the user input config
        this.currentState.singleSelection
      : // If eq has a value is single
        this.currentState != null && this.currentState.eq != null;
  }

  private minValueChanged() {
    const minState = this.minService.getState();
    const newMinState = { periodValue: minState.periodValue, periodYear: minState.periodYear };
    if (!this.isSingleSelection) {
      const currMaxState = this.maxService.getState();
      this.maxService.setStateAndNotify({
        ...currMaxState,
        minValue: newMinState,
      });
    }
    if (this.isSingleSelection) {
      this.currentState.eq = newMinState;
    } else {
      this.currentState.gte = newMinState;
    }
    this.setIsValidState();
    this.finishSelection();
  }

  private maxValueChanged() {
    const maxState = this.maxService.getState();
    const newMaxState = { periodValue: maxState.periodValue, periodYear: maxState.periodYear };
    this.currentState.lte = newMaxState;
    this.setIsValidState();
    this.finishSelection();
  }

  private setIsValidState() {
    this.isMaxStateValid = this.maxService ? this.maxService.isAValidState : false;
    this.isAValidState = this.maxService
      ? this.isMaxStateValid && this.minService.isAValidState
      : this.minService.isAValidState;
  }
}
